const moment = require("moment-timezone");

const parseDateStrIntoParts = (dateStr) => {
  const date = new Date(dateStr);
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: 0,
  };
};

/**
 * Given a set of end and start times and dates:
 * 1. Combine them together to create a single start and end moment
 * 2. Split the start and time moments back up into their respective start and end times and dates.
 */
const constructUTCTimes = ({
  startTime,
  endTime,
  startDate,
  endDate,
  timezone_id,
  isRecurring,
}) => {
  // Parse the time strings in the provided timezone
  const startTimeParts = parseDateStrIntoParts(startTime);
  const endTimeParts = parseDateStrIntoParts(endTime);
  const startDateParts = parseDateStrIntoParts(startDate);

  const combinedStartDateTime = moment
    .tz(timezone_id)
    .year(startDateParts.year)
    .month(startDateParts.month)
    .date(startDateParts.date)
    .hour(startTimeParts.hours)
    .minute(startTimeParts.minutes)
    .second(startTimeParts.seconds);

  let combinedEndDateTime = null;
  let combinedEndDateStartTime = null;
  if (endDate) {
    const endDateParts = parseDateStrIntoParts(endDate);
    combinedEndDateTime = moment
      .tz(timezone_id)
      .year(endDateParts.year)
      .month(endDateParts.month)
      .date(endDateParts.date)
      .hour(endTimeParts.hours)
      .minute(endTimeParts.minutes)
      .second(endTimeParts.seconds);
    combinedEndDateStartTime = moment
      .tz(timezone_id)
      .year(endDateParts.year)
      .month(endDateParts.month)
      .date(endDateParts.date)
      .hour(startTimeParts.hours)
      .minute(startTimeParts.minutes)
      .second(startTimeParts.seconds);
  } else {
    combinedEndDateTime = moment
      .tz(timezone_id)
      .year(startDateParts.year)
      .month(startDateParts.month)
      .date(startDateParts.date)
      .hour(endTimeParts.hours)
      .minute(endTimeParts.minutes)
      .second(endTimeParts.seconds);
  }

  // Convert to UTC
  const startUTC = combinedStartDateTime.utc();
  const endUTC = combinedEndDateTime.utc();



  return {
    startDate: startUTC.format('YYYY-MM-DD'),
    startTime: startUTC.format('HH:mm:ss'),
    endTime: endUTC.format('HH:mm:ss'),
    // `endDate` may be null, thus the falsey AND check
    endDate:
      endDate &&
      (isRecurring
        ? combinedEndDateStartTime.utc().format('YYYY-MM-DD')
        : endUTC.format('YYYY-MM-DD')),
  };
};

/**
 * Offset a foorun's start and end moment to account for DST if applicable. The
 * `sourceTimezoneId` is the timezone in which the food run was scheduled.
 */
function offsetForDST(start, end, sourceTimezoneId, utcInsertedOnDst) {
  start.tz(sourceTimezoneId);
  end.tz(sourceTimezoneId);

  const startIsDST = start.isDST();
  const endIsDST = end.isDST();

  start.utc();
  end.utc();

  let newStartDate = start.clone();
  let newEndDate = end.clone();
  if (startIsDST && !utcInsertedOnDst) {
    // The start time is in DST
    // UTC time from DB was inserted during a standard day
    const originalStartDate = start.format("YYYY-MM-DD");
    const newTimeString = start.clone().subtract(1, "hours").format("HH:mm:00");
    newStartDate = moment.utc(`${originalStartDate} ${newTimeString}`);
  } else if (!startIsDST && utcInsertedOnDst) {
    // The start time is in standard time
    // UTC time from DB was inserted on a DST day.

    /**
     * !! NOTE !!
     * start.add(1, "hours").format("YYYY-MM-DD HH:mm:00") would increment the date
     * if the utc time was 2022-11-10 23:00:00,
     * then start.add(1, "hours").format("YYYY-MM-DD HH:mm:00") would result in 2022-11-11 00:00:00
     * we don't want the date to be incremented, just the timestring should be incremented to next hour
     */
    const originalStartDate = start.format("YYYY-MM-DD");
    const newTimeString = start.clone().add(1, "hours").format("HH:mm:00");
    newStartDate = moment.utc(`${originalStartDate} ${newTimeString}`);
  }

  if (endIsDST && !utcInsertedOnDst) {
    // The end time is in DST
    // UTC time from DB was inserted during a standard day
    const originalEndDate = end.format("YYYY-MM-DD");
    const newTimeString = end.clone().subtract(1, "hours").format("HH:mm:00");
    newEndDate = moment.utc(`${originalEndDate} ${newTimeString}`);
  } else if (!endIsDST && utcInsertedOnDst) {
    // The end time is in standard time
    // UTC time from DB was inserted on a DST day.
    const originalEndDate = end.format("YYYY-MM-DD");
    const newTimeString = end.clone().add(1, "hours").format("HH:mm:00");
    newEndDate = moment.utc(`${originalEndDate} ${newTimeString}`);
  }

  return {
    start: newStartDate.format("YYYY-MM-DD HH:mm:00"),
    end: newEndDate.format("YYYY-MM-DD HH:mm:00"),
  };
}

module.exports = { offsetForDST, constructUTCTimes };
